// src/components/homepage/TestimonialsSection.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { fadeIn } from '../../utils/gsapAnimations';
import SectionLoader from '../common/SectionLoader';
import { useContent } from '../../context/ContentContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

gsap.registerPlugin(Draggable);

function TestimonialsSection() {
  const { content, errors, loading: globalLoading } = useContent();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const loopRef = useRef(null);
  const autoTweenRef = useRef(null);

  const testimonials = content?.testimonials?.testimonials || [];
  const duplicated = [...testimonials, ...testimonials];

  // Fade in
  useEffect(() => {
    if (sectionRef.current) fadeIn(sectionRef.current, 0.4);
  }, []);

  useEffect(() => {
    if (globalLoading || testimonials.length === 0) return;
    if (!trackRef.current || !containerRef.current) return;

    const track = trackRef.current;
    const container = containerRef.current;

    const cards = Array.from(track.children);
    const itemWidth =
      cards[0].offsetWidth +
      parseFloat(getComputedStyle(cards[0]).marginRight || 0);
    const totalWidth = itemWidth * testimonials.length;

    // Initial position
    gsap.set(track, { x: 0 });

    // Auto-scroll function using gsap tick
    function autoScroll() {
      gsap.to(track, {
        x: `-=${0.5}`, // auto scroll speed
        duration: 0.016, // 60fps tick
        ease: 'none',
        modifiers: {
          x: (x) => gsap.utils.wrap(-totalWidth, 0, parseFloat(x)) + 'px',
        },
        onComplete: autoScroll,
      });
    }

    autoScroll();

    // Draggable with inertia
    Draggable.create(track, {
      type: 'x',
      inertia: true,
      onPress: () => gsap.globalTimeline.pause(),
      onDrag: function () {
        gsap.set(track, { x: gsap.utils.wrap(-totalWidth, 0, this.x) });
      },
      onThrowUpdate: function () {
        gsap.set(track, { x: gsap.utils.wrap(-totalWidth, 0, this.x) });
      },
      onRelease: () => gsap.globalTimeline.resume(),
    });

    // Hover pause
    const handlePause = () => gsap.globalTimeline.pause();
    const handleResume = () => gsap.globalTimeline.resume();
    container.addEventListener('mouseenter', handlePause);
    container.addEventListener('mouseleave', handleResume);

    loopRef.current = {
      next: () => move(1),
      previous: () => move(-1),
    };

    function move(direction) {
      gsap.to(track, {
        x: `+=${-direction * itemWidth}`,
        duration: 0.6,
        ease: 'power2.out',
        modifiers: {
          x: (x) => gsap.utils.wrap(-totalWidth, 0, parseFloat(x)) + 'px',
        },
      });
    }

    return () => {
      container.removeEventListener('mouseenter', handlePause);
      container.removeEventListener('mouseleave', handleResume);
    };
  }, [globalLoading, testimonials.length]);

  const movePrev = () => loopRef.current?.previous?.();
  const moveNext = () => loopRef.current?.next?.();

  if (globalLoading)
    return <SectionLoader message="Loading testimonials..." />;

  const testimonialsError =
    errors?.testimonials ||
    (testimonials.length === 0 ? 'No testimonials found' : null);

  if (testimonialsError) {
    return (
      <section ref={sectionRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-lg text-gray-600">{testimonialsError}</p>
        </div>
      </section>
    );
  }

  const showArrows = testimonials.length > 2;

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden relative"
    >
      <div className="max-w-7xl mx-auto px-6 relative">
        <h2 className="text-3xl md:text-4xl font-playfair-display font-semibold text-primary text-center mb-14">
          What People Say
        </h2>

        {showArrows && (
          <>
            <button
              onClick={movePrev}
              className="hidden lg:flex items-center justify-center absolute top-1/2 -translate-y-1/2 left-0 z-20 w-12 h-12 bg-white rounded-full shadow-lg hover:scale-110 transition text-primary text-xl"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              onClick={moveNext}
              className="hidden lg:flex items-center justify-center absolute top-1/2 -translate-y-1/2 right-0 z-20 w-12 h-12 bg-white rounded-full shadow-lg hover:scale-110 transition text-primary text-xl"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </>
        )}

        <div
          ref={containerRef}
          className="relative overflow-hidden cursor-grab active:cursor-grabbing"
        >
          {/* Edge fade effect */}
          <div className="pointer-events-none absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-white to-transparent z-10" />

          <div
            ref={trackRef}
            className="flex gap-8 will-change-transform"
            style={{ transform: 'translate3d(0,0,0)' }}
          >
            {duplicated.map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="flex-shrink-0 w-[320px] sm:w-[380px] lg:w-[420px] bg-white rounded-3xl p-8 shadow-md border border-gray-100 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
              >
                <div className="flex items-center gap-4 mb-6">
                  {testimonial.image && (
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL}${testimonial.image}`}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                      loading="lazy"
                    />
                  )}
                  <div>
                    <p className="font-montserrat font-semibold text-primary text-lg">
                      {testimonial.name}
                    </p>
                    {testimonial.role && (
                      <p className="text-sm text-secondary">{testimonial.role}</p>
                    )}
                  </div>
                </div>

                <p className="text-secondary italic leading-relaxed text-base line-clamp-6">
                  “{testimonial.testimonial}”
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TestimonialsSection;
